'use client';

import { useState, useCallback, useMemo, memo, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/hooks/useAuth';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Alert,
  LinearProgress,
  Divider,
  Stack,
} from '@mui/material';

import { lusitana } from '@/components/app/proyectos/raceway/fonts';
import { fase1Info, fase2Info, fase3Info, leyendaValvulas } from '@/data/proyectos/raceway/racewayData';
import { useRacewayWebSocket } from '@/hooks/raceway/useRacewayWebSocket';
import { toggleValvula } from '@/lib/proyectos/raceway/raceway';
import { getValveOpen } from '@/types/proyectos/raceway';
import FaseCard from '@/components/app/proyectos/raceway/FaseCard';
import FuncionInfoCard from '@/components/app/proyectos/raceway/FuncionInfoCard';
import RacewaySVG from '@/components/app/proyectos/raceway/RacewaySVG';
import PageSkeleton from '@/components/app/proyectos/raceway/pageskeleton';
import TimeSettingsPanel from '@/components/app/proyectos/raceway/Timesettingspanel';

const MemoizedFaseCard         = memo(FaseCard);
const MemoizedRacewaySVG       = memo(RacewaySVG);
const MemoizedTimeSettingsPanel = memo(TimeSettingsPanel);

export default function Page() {
  const URL_WS = process.env.NEXT_PUBLIC_RACEWAY_WS;
  const router  = useRouter();
  const { user, loading: authLoading, canAccessRaceway } = useAuth();

  const hasAccess = useMemo(
    () => !!user && (user.role === 'ADMIN' || user.role === 'RACEWAY_USER'),
    [user],
  );

  useEffect(() => {
    if (!authLoading) {
      if (!user) router.push('/login');
      else if (!canAccessRaceway) router.push('/dashboard');
    }
  }, [user, authLoading, canAccessRaceway, router]);

  const { estado, error: errorEstado, ultimaActualizacion } = useRacewayWebSocket(
    canAccessRaceway ? (URL_WS ?? null) : null,
  );

  const [cargandoValvula, setCargandoValvula] = useState<string | null>(null);
  const [mensajeBloqueo, setMensajeBloqueo]   = useState<string | null>(null);

  const gv = useCallback(
    (addr: number) => getValveOpen(estado?.valves, addr),
    [estado?.valves],
  );

  const handleValvula = useCallback(
    async (valvula: string, estadoActual: boolean) => {
      setCargandoValvula(valvula);
      try {
        await toggleValvula(valvula, !estadoActual);
        setMensajeBloqueo(null);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Error desconocido';
        setMensajeBloqueo(msg);
        setTimeout(() => setMensajeBloqueo(null), 3_000);
      } finally {
        setCargandoValvula(null);
      }
    },
    [],
  );

  const raceway = useMemo(() => estado?.tanks?.dep_raceway, [estado]);
  const cultivo = useMemo(() => estado?.tanks?.dep_cultivo, [estado]);
  const cosecha = useMemo(() => estado?.tanks?.dep_cosecha, [estado]);

  const niveles = useMemo(
    () => [
      { label: 'Raceway',      nivel: raceway?.level ?? 0, color: '#22c55e' },
      { label: 'Dep. Cultivo', nivel: cultivo?.level ?? 0, color: '#a855f7' },
      { label: 'Dep. Cosecha', nivel: cosecha?.level ?? 0, color: '#0ea5e9' },
    ],
    [raceway?.level, cultivo?.level, cosecha?.level],
  );

  const fasesConfig = useMemo(
    () => [
      {
        fase: 1,
        deposito: 'Dep. Raceway',
        color: 'from-blue-800 to-blue-700',
        valvulas: [
          {
            id:        'v1',
            label:     'Llenado',
            abierta:   gv(16),
            bloqueada: raceway?.sensor_max ?? false,
          },
        ],
        vaciadoManual: true,
      },
      {
        fase: 2,
        deposito: 'Dep. Cultivo',
        color: 'from-blue-600 to-blue-500',
        valvulas: [
          { id: 'v2', label: 'Llenado',           abierta: gv(17) },
          { id: 'v3', label: 'Gases O₂+CO₂',      abierta: gv(18) },
          { id: 'v4', label: 'Vuelca al Raceway',  abierta: gv(19) },
          { id: 'v5', label: 'Vaciado exterior',   abierta: gv(20) },
        ],
        vaciadoManual: false,
      },
      {
        fase: 3,
        deposito: 'Dep. Cosecha',
        color: 'from-blue-400 to-blue-300',
        valvulas: [
          {
            id:        'v6',
            label:     'Llenado',
            abierta:   gv(21),
            bloqueada: cosecha?.sensor_max ?? false,
          },
        ],
        vaciadoManual: true,
      },
    ],
    [raceway?.sensor_max, cosecha?.sensor_max, gv],
  );

  if (authLoading || !hasAccess) return <PageSkeleton />;
  if (!estado && !errorEstado)   return <PageSkeleton />;

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: '#fff', py: 4 }}>
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>

        {/* Header */}
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <Typography
            variant="h2"
            className={lusitana.className}
            sx={{
              fontWeight: 'bold',
              color: '#1d4ed8',
              fontSize: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            Gestión del Raceway
          </Typography>
          {ultimaActualizacion && (
            <Typography variant="caption" sx={{ color: 'text.disabled', mt: 1, display: 'block' }}>
              Actualizado: {ultimaActualizacion}
            </Typography>
          )}
        </Box>

        {/* Alertas */}
        <Stack spacing={2} sx={{ mb: 3 }}>
          {errorEstado && (
            <Alert severity="error" variant="outlined" sx={{ borderRadius: '12px' }}>
              {errorEstado}
            </Alert>
          )}
          {mensajeBloqueo && (
            <Alert severity="warning" variant="filled" sx={{ borderRadius: '12px' }}>
              Acción bloqueada: {mensajeBloqueo}
            </Alert>
          )}
        </Stack>

        {/* Leyenda */}
        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 3 }}>
          {leyendaValvulas.map((l) => (
            <Box key={l.texto} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12, height: 12, borderRadius: '50%',
                  bgcolor: l.color.includes('algae')
                    ? '#22c55e'
                    : l.color.includes('red')
                    ? '#f87171'
                    : l.color.includes('gray')
                    ? '#d1d5db'
                    : '#94a3b8',
                }}
              />
              <Typography variant="body2" color="text.secondary">{l.texto}</Typography>
            </Box>
          ))}
        </Box>

        {/* SVG + Panel lateral */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: '0 0 72%', minWidth: 0 }}>
            <MemoizedRacewaySVG estado={estado} onValvula={handleValvula} />
          </Box>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            {/* ↓ Pasamos estado.tanks para que el panel muestre la fase activa */}
            <MemoizedTimeSettingsPanel tanks={estado?.tanks} />
          </Box>
        </Box>

        {/* Barras de nivel */}
        {estado && (
          <Grid container spacing={2} sx={{ mt: 3 }}>
            {niveles.map((item) => (
              <Grid size={{ xs: 12, sm: 4 }} key={item.label}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600, color: 'text.secondary',
                      display: 'block', mb: 1,
                      textTransform: 'uppercase', letterSpacing: 1,
                    }}
                  >
                    {item.label}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(item.nivel, 100)}
                    sx={{
                      height: 6, borderRadius: 5, bgcolor: '#f1f5f9',
                      '& .MuiLinearProgress-bar': { bgcolor: item.color },
                    }}
                  />
                  <Typography variant="subtitle2" sx={{ textAlign: 'right', mt: 1, fontWeight: 'bold' }}>
                    {item.nivel.toFixed(1)}%
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Tarjetas de fase */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Grid
            container
            spacing={3}
            sx={{ maxWidth: '1200px', justifyContent: 'center', alignItems: 'stretch' }}
          >
            {fasesConfig.map((card) => (
              <Grid
                key={card.fase}
                size={{ xs: 12, sm: 6, md: 4, lg: 3.5 }}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <Box sx={{ width: '100%', maxWidth: '320px', display: 'flex' }}>
                  <MemoizedFaseCard
                    fase={card.fase}
                    deposito={card.deposito}
                    color={card.color}
                    valvulas={card.valvulas}
                    vaciadoManual={card.vaciadoManual}
                    onValvula={handleValvula}
                    cargando={cargandoValvula}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Secciones informativas */}
        {[
          { title: 'Fase 1 — Dep. Raceway', data: fase1Info },
          { title: 'Fase 2 — Dep. Cultivo', data: fase2Info },
          { title: 'Fase 3 — Dep. Cosecha', data: fase3Info },
        ].map((seccion, idx) => (
          <Box key={idx} sx={{ mt: 8 }}>
            <Divider sx={{ mb: 6 }} />
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                className={lusitana.className}
                sx={{ color: '#1d4ed8', fontWeight: 'bold' }}
              >
                {seccion.title}
              </Typography>
              <Box sx={{ width: 64, height: 1, bgcolor: '#bfdbfe', mx: 'auto', mt: 2 }} />
            </Box>
            <Stack spacing={2}>
              {seccion.data.map((fn, i) => (
                <FuncionInfoCard key={fn.id} fn={fn} index={i} />
              ))}
            </Stack>
          </Box>
        ))}

      </Container>
    </Box>
  );
}